// src/context/TeamContext.js
import { createContext, useContext, useState, useEffect, useRef } from "react";
import Cryptique from "cryptique-sdk";
import teamService from '../services/teamService';
import axiosInstance from '../services/api/axiosInstance';

const TeamContext = createContext();

const isPublicAuthRoute = () => {
  const path = window.location?.pathname || '';
  return (
    path.startsWith('/login') ||
    path.startsWith('/signup') ||
    path.startsWith('/reset-password')
  );
};

const isJwtExpiredOrInvalid = (token) => {
  try {
    if (!token || typeof token !== 'string') return true;
    const parts = token.split('.');
    if (parts.length !== 3) return true;
    const payload = JSON.parse(atob(parts[1].replace(/-/g, '+').replace(/_/g, '/')));
    const expSeconds = payload?.exp;
    if (!expSeconds) return true;
    const nowMs = Date.now();
    // Add small skew so we don't race near expiry
    return expSeconds * 1000 <= nowMs + 15_000;
  } catch (_) {
    return true;
  }
};

export const TeamProvider = ({ children }) => {
  const [selectedTeam, setSelectedTeam] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  
  // Debounce timeout ref
  const debounceTimeoutRef = useRef(null);

  // Function to fetch complete team data using the centralized service
  const fetchCompleteTeamData = async (teamId) => {
    try {
      return await teamService.fetchTeamData(teamId);
    } catch (error) {
      console.error('Error fetching complete team data:', error);
      return null;
    }
  };

  // Function to fetch subscription data using the centralized service
  const fetchSubscriptionData = async (teamId) => {
    try {
      return await teamService.fetchSubscriptionData(teamId);
    } catch (error) {
      console.error('Error fetching subscription data:', error);
      return null;
    }
  };

  // Enhanced function to load team data with subscription information
  const loadTeamDataWithSubscription = async (teamData) => {
    try {
      if (!teamData || (!teamData.id && !teamData._id)) {
        return teamData;
      }
      
      // Fetch subscription data in parallel, but only if not cached
      const [completeTeamData, subscriptionData] = await Promise.all([
        fetchCompleteTeamData(teamData.id || teamData._id),
        fetchSubscriptionData(teamData.id || teamData._id)
      ]);
      
      // Merge the complete data
      const enhancedTeamData = {
        ...teamData,
        ...(completeTeamData || {}),
        // subscriptionData is the billing-status payload (effectivePlan/plan/status/etc).
        // Keep it at `selectedTeam.subscription` so the rest of the app can read it consistently.
        subscription: subscriptionData || teamData.subscription || null
      };
      
      return enhancedTeamData;
    } catch (error) {
      console.error('Error loading team data with subscription:', error);
      return teamData; // Return original data if enhancement fails
    }
  };

  // Debounced version of loadTeamDataWithSubscription
  const debouncedLoadTeamData = (teamData, callback) => {
    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
    }
    
    debounceTimeoutRef.current = setTimeout(async () => {
      const enhancedTeam = await loadTeamDataWithSubscription(teamData);
      callback(enhancedTeam);
    }, 300); // 300ms debounce
  };

  useEffect(() => {
    const loadTeamData = async () => {
      try {
        const teamData = localStorage.getItem('selectedTeamData');
        if (teamData) {
          const parsedTeam = JSON.parse(teamData);

          // Avoid protected API calls on public auth routes (login/signup/reset).
          // This prevents noisy 401s before the user authenticates.
          if (isPublicAuthRoute()) {
            setSelectedTeam(parsedTeam);
            setIsLoading(false);
            return;
          }

          // Only attempt protected refresh calls when we have a non-expired access token.
          const token = localStorage.getItem('accessToken') || localStorage.getItem('token');
          if (isJwtExpiredOrInvalid(token)) {
            setSelectedTeam(parsedTeam);
            setIsLoading(false);
            return;
          }
          
          // Check if data needs refresh (only if older than 15 minutes or missing subscription)
          // Also refresh if onboarding data is missing, null, or incomplete (to get latest status from DB)
          const needsRefresh = !parsedTeam.subscription || 
            !parsedTeam.onboarding || parsedTeam.onboarding === null || // Refresh if onboarding data is missing or null
            (parsedTeam.isOwner && parsedTeam.onboarding && parsedTeam.onboarding.isComplete === undefined) || // Refresh if isOwner but onboarding status unclear
            !parsedTeam._lastFetched || 
            (Date.now() - parsedTeam._lastFetched > 15 * 60 * 1000); // 15 minutes
          
          if (needsRefresh) {
            // Use debounced loading to prevent rapid successive calls
            debouncedLoadTeamData(parsedTeam, (enhancedTeam) => {
              // Add timestamp to track when data was last fetched
              enhancedTeam._lastFetched = Date.now();
              setSelectedTeam(enhancedTeam);
              
              // Update localStorage with enhanced data and timestamp
              localStorage.setItem('selectedTeamData', JSON.stringify(enhancedTeam));
              
              // Set loading to false after team data is loaded
              setIsLoading(false);
            });
          } else {
            // Use cached data directly without fetching
            setSelectedTeam(parsedTeam);
            setIsLoading(false);
          }
        } else {
          // If no team data, try to get basic team info
          const teamName = localStorage.getItem('selectedTeam');
          if (teamName) {
            setSelectedTeam({ name: teamName });
          }
          setIsLoading(false);
        }
      } catch (error) {
        console.error('Error loading team data:', error);
        // Fallback to basic team name if available
        const teamName = localStorage.getItem('selectedTeam');
        if (teamName) {
          setSelectedTeam({ name: teamName });
        }
        setIsLoading(false);
      }
    };

    loadTeamData();

    // Listen for changes to selectedTeamData in localStorage (debounced)
    const handleStorageChange = (e) => {
      if (e.key === 'selectedTeamData' || e.key === 'selectedTeam') {
        // Debounce storage changes to prevent rapid reloads
        if (debounceTimeoutRef.current) {
          clearTimeout(debounceTimeoutRef.current);
        }
        debounceTimeoutRef.current = setTimeout(() => {
          setIsLoading(true);
          loadTeamData();
        }, 500);
      }
    };
    
    // Listen for the custom teamDataUpdated event (primary method)
    const handleTeamDataUpdated = async (e) => {
      if (e.detail && e.detail.teamData) {
        // Use debounced loading to prevent rapid successive calls
        debouncedLoadTeamData(e.detail.teamData, (enhancedTeam) => {
          setSelectedTeam(enhancedTeam);
          
          // Update localStorage with enhanced data
          localStorage.setItem('selectedTeamData', JSON.stringify(enhancedTeam));
          
          setIsLoading(false);
        });
      }
    };

    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('teamDataUpdated', handleTeamDataUpdated);
    
    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('teamDataUpdated', handleTeamDataUpdated);
      // Clear any pending debounced calls
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
    };
  }, []);

  // After login / team load: identify (email), people, group membership, group profile.
  // People use `name` / `email` — $name / $email are reserved and rejected by the API.
  useEffect(() => {
    if (isLoading) return;
    if (isPublicAuthRoute()) return;

    const token = localStorage.getItem("accessToken") || localStorage.getItem("token");
    if (!token || isJwtExpiredOrInvalid(token)) return;

    const teamId = selectedTeam?._id || selectedTeam?.id;
    if (!teamId) return;

    let user;
    try {
      user = JSON.parse(localStorage.getItem("User") || "{}");
    } catch {
      return;
    }
    const email = (user.email || "").trim();
    if (!email) return;

    // Derive plan from the billing-status payload (may be effectivePlan or plan).
    const plan = selectedTeam?.subscription?.effectivePlan || selectedTeam?.subscription?.plan || null;
    const teamname = selectedTeam?.name || "";
    const teamIdStr = String(teamId);
    let cancelled = false;

    (async () => {
      try {
        if (typeof Cryptique.ready === "function") {
          await Cryptique.ready();
        }
        // Ensures session is confirmed in storage before identify/people/groups
        await Cryptique.track("cryptique_app_session", {});
        if (cancelled) return;

        const identifyResult = await Cryptique.identify(email);
        if (cancelled) return;
        if (identifyResult && identifyResult.success === false) return;

        const displayName = user.name || user.displayName || user.fullName || "";
        await Cryptique.people.setOnce({
          name: displayName,
          email,
        });
        if (cancelled) return;

        await Cryptique.add_group("team", teamIdStr);
        if (cancelled) return;
        await Cryptique.set_group("team", teamIdStr);
        if (cancelled) return;

        let teamsize = 0;
        try {
          const res = await axiosInstance.get(`/team/${teamIdStr}/members`);
          teamsize = (res.data?.members || []).length;
        } catch {
          /* optional */
        }
        if (cancelled) return;

        const grp = Cryptique.get_group("team", teamIdStr);
        if (grp) {
          const props = {
            teamsize,
            teamname,
            ...(plan ? { plan } : {}),
          };
          await grp.set(props);
        }
      } catch (err) {
        console.error("[Cryptique] team/user sync failed:", err);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [
    isLoading,
    selectedTeam?._id,
    selectedTeam?.id,
    selectedTeam?.name,
    selectedTeam?.subscription?.effectivePlan,
    selectedTeam?.subscription?.plan,
  ]);

  const updateSelectedTeam = async (team) => {
    if (team) {
      // Use debounced loading to prevent rapid successive calls
      debouncedLoadTeamData(team, (enhancedTeam) => {
        // Add timestamp to track when data was last fetched
        enhancedTeam._lastFetched = Date.now();
        setSelectedTeam(enhancedTeam);
        
        // Update both localStorage keys for compatibility
        localStorage.setItem('selectedTeamData', JSON.stringify(enhancedTeam));
        localStorage.setItem('selectedTeam', enhancedTeam.name || team.name || team);
        
        // Dispatch the custom event to notify other components
        window.dispatchEvent(new CustomEvent('teamDataUpdated', { 
          detail: { teamData: enhancedTeam }
        }));
      });
    } else {
      setSelectedTeam(null);
      localStorage.removeItem('selectedTeamData');
      localStorage.removeItem('selectedTeam');
    }
  };

  return (
    <TeamContext.Provider value={{ selectedTeam, setSelectedTeam: updateSelectedTeam, isLoading }}>
      {children}
    </TeamContext.Provider>
  );
};

export const useTeam = () => {
  const context = useContext(TeamContext);
  if (context === undefined) {
    throw new Error('useTeam must be used within a TeamProvider');
  }
  return context;
};